import { templateLicenseMonthlySubscriptionTrialSubForm } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { LicenseMonthlySubscriptionTrialSubFormWebComponent } from '../component/LicenseMonthlySubscriptionTrialSubFormWebComponent';
import { LicenseMonthlySubscriptionTrialSubFormView } from '../view/LicenseMonthlySubscriptionTrialSubFormView';
import { isHidden, toggleVisibility } from '../../../utils/htmlElement.utils';
import { ToggleEvents } from '../../../ui/gzToggle/types';
import { CompanyType } from '../../../company/types/company-type';
import { FormEvent } from '../../../ui/gzForm/formField/types/form-event.enum';
import { LicenseMainFormViewData } from '../../licenseMainForm/types/LicenseMainFormViewData';
import { MonthlyProductType } from '../../types/MonthlyProductType';
import { InputEvents } from '../../../ui/gzInput/types';
import { CompanyData } from '../../../company/types/company-data';
import { DisableReservedSeatsOptionConfirmationWindow } from '../../../company/common/disableReservedSeatsOptionConfirmationWindow/DisableReservedSeatsOptionConfirmationWindow';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { AddonFormType } from '../../common/addonsForm/types/AddonFormType';
import { AddonFormEvent } from '../../common/addonsForm/types/AddonFormEvent';

export class LicenseMonthlySubscriptionTrialSubFormController extends BaseWebComponentController {
  public component: LicenseMonthlySubscriptionTrialSubFormWebComponent;
  public view: LicenseMonthlySubscriptionTrialSubFormView;
  public viewConstructor: typeof LicenseMonthlySubscriptionTrialSubFormView;

  private childCompanyType: CompanyType;
  private selectedUsableProduct: MonthlyProductType;

  private isDisableReservedSeatsOptionConfirmationWindowAllowed = false;

  constructor(component: LicenseMonthlySubscriptionTrialSubFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicenseMonthlySubscriptionTrialSubFormView,
      viewTemplate: templateLicenseMonthlySubscriptionTrialSubForm,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$ownUseProductTypeFormField,
        event: FormEvent.ValueChange,
        callback: this.onAssignedProductTypeChange,
      },
      {
        element: this.view.dom.$resellingProductEndpointSecurityToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onProductEndpointSecurityToggleChange,
      },
      {
        element: this.view.dom.$resellingProductBitdefenderEdrToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onProductBitdefenderEdrToggleChange,
      },

      // Reserve Seats toggle
      {
        element: this.view.dom.$reserveSeatsFormField,
        event: FormEvent.ToggleableChange,
        callback: this.onToggleReserveSeats,
      },
      {
        element: this.view.dom.$reserveSeatsFormField,
        event: InputEvents.CHANGE,
        callback: this.onReserveSeatsValueChange,
      },

      // Addon forms
      {
        element: this.view.dom.$ownUseMonthlyAddonsForm,
        event: AddonFormEvent.ViewUpdate,
        callback: this.setAddonFormsVisibility,
      },
      {
        element: this.view.dom.$resellingMonthlyAddonsForm,
        event: AddonFormEvent.ViewUpdate,
        callback: this.setAddonFormsVisibility,
      },
      {
        element: this.view.dom.$ownUseMonthlyAddonsForm,
        event: AddonFormEvent.ToggleUpdate,
        callback: this.updateResellingFormFromOwnUse,
      },
      {
        element: this.view.dom.$resellingMonthlyAddonsForm,
        event: AddonFormEvent.ToggleUpdate,
        callback: this.updateOwnUseFormFromReselling,
      },
    ];
  }

  public setCompanyType(companyType: CompanyType, isInitialValueSet: boolean) {
    this.childCompanyType = companyType;
    this.updateDelegateSection(isInitialValueSet);

    this.view.dom.$ownUseMonthlyAddonsForm.childCompanyType = companyType;
    this.view.dom.$resellingMonthlyAddonsForm.childCompanyType = companyType;
  }

  public setViewData(viewData: LicenseMainFormViewData) {
    this.view.dom.$ownUseMonthlyAddonsForm.formType = AddonFormType.OwnUse;
    this.view.dom.$ownUseMonthlyAddonsForm.isTrial = true;
    this.view.dom.$resellingMonthlyAddonsForm.formType = AddonFormType.Resell;
    this.view.dom.$resellingMonthlyAddonsForm.isTrial = true;

    this.view.dom.$ownUseMonthlyAddonsForm.availableFeatures = viewData.companyLicenseInformation.toggleableFeatures;
    this.view.dom.$resellingMonthlyAddonsForm.availableFeatures = viewData.companyLicenseInformation.toggleableFeatures;

    this.view.dom.$subscriptionDetailsAccordion.isOpen = true;

    const isPartner = this.childCompanyType === CompanyType.Partner;
    this.view.dom.$ownUseAccordion.isOpen = !isPartner;

    if (!isHidden(this.view.dom.$resellingAccordion)) {
      this.view.dom.$resellingAccordion.isOpen = false;
    }

    this.view.setViewData(viewData);
  }

  public setCompanyData(company: CompanyData) {
    // Setting initial company type when editing a company
    this.setCompanyType(CompanyData.getCompanyType(company), true);

    this.view.setCompanyData(company);

    // Setting initial trigger for dialog from company data
    this.setUpDisableReservedSeatsOptionConfirmationWindow(company);
  }

  private updateDelegateSection(isInitialValueSet: boolean) {
    const isCreatingPartner = this.childCompanyType === CompanyType.Partner;

    this.view.dom.$ownUseAccordion.isOpen = !isCreatingPartner;
    toggleVisibility(this.view.dom.$resellingAccordion, isCreatingPartner);

    if (!isInitialValueSet) {
      this.view.dom.$resellingProductEndpointSecurityToggle.value =
        isCreatingPartner && this.selectedUsableProduct === MonthlyProductType.EndpointSecurity;

      this.view.dom.$resellingProductBitdefenderEdrToggle.value =
        isCreatingPartner && this.selectedUsableProduct === MonthlyProductType.BitdefenderEdr;
    }
  }

  // Event handlers

  private onAssignedProductTypeChange(event: Event): void {
    const customEvent = event as CustomEvent<{ isUserAction: boolean; value: MonthlyProductType }>;
    const isUserAction = customEvent.detail.isUserAction;

    const isPartner = this.childCompanyType === CompanyType.Partner;
    const newSelectedProduct = this.view.dom.$ownUseProductTypeFormField.value as MonthlyProductType;

    if (this.selectedUsableProduct === newSelectedProduct) {
      return;
    }

    this.selectedUsableProduct = newSelectedProduct;
    this.view.dom.$ownUseMonthlyAddonsForm.usableProduct = newSelectedProduct;
    this.view.dom.$resellingMonthlyAddonsForm.usableProduct = newSelectedProduct;

    switch (this.selectedUsableProduct) {
      case MonthlyProductType.BitdefenderEdr:
        // Force Bitdefender EDR toggle to be 'on'
        this.view.dom.$resellingProductBitdefenderEdrToggle.value = isPartner;

        if (isUserAction) {
          this.view.dom.$resellingProductEndpointSecurityToggle.value = false;
        }

        // In-memory toggle for companion usable
        this.view.companionUsableToggle.value = true;

        // Enable Endpoint Security toggle and disable Bitdefender Edr toggle
        this.view.dom.$resellingProductBitdefenderEdrToggle.readonly = true;
        this.view.dom.$resellingProductEndpointSecurityToggle.readonly = false;

        break;

      case MonthlyProductType.EndpointSecurity:
        // Force Endpoint Security toggle to be 'on'
        if (isUserAction) {
          this.view.dom.$resellingProductBitdefenderEdrToggle.value = false;
        }
        this.view.dom.$resellingProductEndpointSecurityToggle.value = isPartner;

        // In-memory toggle for companion usable
        this.view.companionUsableToggle.value = false;

        // Disable Endpoint Security toggle and enable Bitdefender EDR toggle
        this.view.dom.$resellingProductBitdefenderEdrToggle.readonly = false;
        this.view.dom.$resellingProductEndpointSecurityToggle.readonly = true;

        break;
    }
  }

  private onProductEndpointSecurityToggleChange() {
    this.updateDelegateProductsInAddonForms();
  }

  private onProductBitdefenderEdrToggleChange() {
    this.updateDelegateProductsInAddonForms();
  }

  private updateDelegateProductsInAddonForms(): void {
    this.view.dom.$ownUseMonthlyAddonsForm.delegableProducts = this.getDelegableProducts();
    this.view.dom.$resellingMonthlyAddonsForm.delegableProducts = this.getDelegableProducts();
  }

  private getDelegableProducts(): MonthlyProductType[] {
    const delegableProducts: MonthlyProductType[] = [];

    // customers don't have _any_ delegable products (the toggles should be disabled anyway)
    if (this.childCompanyType === CompanyType.Customer) {
      return delegableProducts;
    }

    if (this.view.dom.$resellingProductEndpointSecurityToggle.value) {
      delegableProducts.push(MonthlyProductType.EndpointSecurity);
    }
    if (this.view.dom.$resellingProductBitdefenderEdrToggle.value) {
      delegableProducts.push(MonthlyProductType.BitdefenderEdr);
    }

    return delegableProducts;
  }

  private onToggleReserveSeats(event: WebComponentEvent<{ isOn: boolean; isUserAction: boolean }>) {
    const isOn = event.detail.isOn;
    const reserveSeatsFormGroup = this.view.formGroup?.getFormChild(['specifics', 'hasReservedSlots']);

    this.view.updateAvailableSeatsText();

    if (!reserveSeatsFormGroup) {
      return;
    }

    const reserveSeatsValueBeforeChange = reserveSeatsFormGroup.value;
    reserveSeatsFormGroup.value = isOn;

    const toggleChanged = reserveSeatsFormGroup.value !== reserveSeatsValueBeforeChange;
    if (toggleChanged && event.detail.isUserAction) {
      this.createAndShowDisableReservedSeatsOptionConfirmationWindowIfAllowed(isOn);
    }
  }

  private onReserveSeatsValueChange(): void {
    this.view.updateAvailableSeatsText();
  }

  private setAddonFormsVisibility(event: Event): void {
    const customEvent = event as CustomEvent<{ formType: AddonFormType; isAnyToggleAvailable: boolean }>;

    if (customEvent.detail.formType === AddonFormType.OwnUse) {
      toggleVisibility(this.view.dom.$ownUseAddonsSection, customEvent.detail.isAnyToggleAvailable);
    } else {
      toggleVisibility(this.view.dom.$resellingAddonsSection, customEvent.detail.isAnyToggleAvailable);
    }
  }

  private updateResellingFormFromOwnUse(event: Event): void {
    const customEvent = event as CustomEvent<{ addonKey: string; value: boolean }>;

    this.view.dom.$resellingMonthlyAddonsForm.controller.updateToggle(
      customEvent.detail.addonKey,
      customEvent.detail.value
    );
  }

  private updateOwnUseFormFromReselling(event: Event): void {
    const customEvent = event as CustomEvent<{ addonKey: string; value: boolean }>;

    this.view.dom.$ownUseMonthlyAddonsForm.controller.updateToggle(
      customEvent.detail.addonKey,
      customEvent.detail.value
    );
  }

  private setUpDisableReservedSeatsOptionConfirmationWindow(company: CompanyData) {
    if (
      this.view.viewData?.context !== CompanyActionContext.Edit ||
      !CompanyData.hasReservedSeatsForUnderneathCompanies(company)
    ) {
      return;
    }
    this.isDisableReservedSeatsOptionConfirmationWindowAllowed =
      CompanyData.hasReservedSeatsForUnderneathCompanies(company);
  }

  private createAndShowDisableReservedSeatsOptionConfirmationWindowIfAllowed(reservedSeatsToggleIsOn: boolean) {
    if (reservedSeatsToggleIsOn || !this.isDisableReservedSeatsOptionConfirmationWindowAllowed) {
      // Set the field agreeRemoveReservedSlotsForChildren to default value false if
      // is not the case to remove reserved seats either because the toggle is switched on or confirmation is not allowed
      this.view.agreeRemoveReservedSlotsForChildren = false;
      return;
    }

    const cancelCallback = () => {
      this.view.agreeRemoveReservedSlotsForChildren = false;
      this.view.updateReservedSeatsFormFieldAvailability(!reservedSeatsToggleIsOn);
    };

    const confirmCallback = () => {
      this.view.agreeRemoveReservedSlotsForChildren = true;
    };
    DisableReservedSeatsOptionConfirmationWindow.showDisableReservedSeatsOptionConfirmationWindow(
      this.dependencies.translateFn,
      cancelCallback,
      confirmCallback
    );
  }
}
